import {
  resolveTaskTypeMetrics,
  MAX_TASK_TYPE_METRICS,
  MAX_TASK_TYPE_METRIC_LABEL_LENGTH,
  isTaskTypeMetricSumFieldType,
  type TaskTypeMetric,
  type TaskTypeMetricKind,
} from '@erp/shared-types';
import type { ProcedureInputSchemaStatus } from './procedureParameterBinding';

/**
 * Editor drafts for a task type's metric cards (the strip above its queue on
 * a Task Page). The editor always shows the effective list: a type with no
 * saved choice starts from the defaults, and saving the defaults unchanged
 * keeps the column null.
 */

export interface TaskTypeMetricDraft {
  clientId: string;
  kind: TaskTypeMetricKind;
  /** Sum cards: the numeric input field; empty until picked. */
  field: string;
  /** Optional title override; blank = the default title. */
  label: string;
}

/** An input field a sum card may total. */
export interface MetricFieldOption {
  fieldName: string;
  label: string;
  fieldType: string;
  /** A list-valued field; a sum card cannot total it. */
  isMultiple?: boolean;
}

export type MetricDraftProblem =
  'missing_field' | 'unknown_field' | 'label_too_long';

let metricDraftCounter = 0;

export function newMetricDraft(
  partial?: Partial<Omit<TaskTypeMetricDraft, 'clientId'>>,
): TaskTypeMetricDraft {
  metricDraftCounter += 1;
  return {
    clientId: `metric-draft-${metricDraftCounter}`,
    kind: 'open_count',
    field: '',
    label: '',
    ...partial,
  };
}

function draftFromMetric(metric: TaskTypeMetric): TaskTypeMetricDraft {
  return newMetricDraft({
    kind: metric.kind,
    field: metric.kind === 'sum' ? metric.field : '',
    label: metric.label ?? '',
  });
}

/** Drafts for the saved cards; none when the type has no cards. */
export function metricDraftsFromStored(
  metrics: readonly TaskTypeMetric[] | null | undefined,
): TaskTypeMetricDraft[] {
  return resolveTaskTypeMetrics(metrics).map(draftFromMetric);
}

function metricFromDraft(draft: TaskTypeMetricDraft): TaskTypeMetric {
  const label = draft.label.trim() || undefined;
  if (draft.kind === 'sum') {
    return { kind: 'sum', field: draft.field, ...(label ? { label } : {}) };
  }
  return { kind: draft.kind, ...(label ? { label } : {}) };
}

/** Comparable form of a draft list (ignores client ids and blank labels). */
export function metricDraftsSignature(
  drafts: readonly TaskTypeMetricDraft[],
): string {
  return JSON.stringify(drafts.map(metricFromDraft));
}

/**
 * Wire value: null keeps a type that never chose cards that way when it still
 * has none; otherwise the list as drafted.
 */
export function metricsForSave(
  drafts: readonly TaskTypeMetricDraft[],
  stored: readonly TaskTypeMetric[] | null | undefined,
): TaskTypeMetric[] | null {
  if (stored == null && drafts.length === 0) return null;
  return drafts.map(metricFromDraft);
}

/**
 * Input fields a sum card may total: single-value integer, float, numeric or
 * currency fields.
 */
export function numericMetricFields(
  fields: readonly MetricFieldOption[],
): MetricFieldOption[] {
  return fields.filter(
    (field) =>
      !field.isMultiple && isTaskTypeMetricSumFieldType(field.fieldType),
  );
}

/**
 * What blocks saving this card. Unknown fields are only reported once the
 * schema is loaded (`numericFields` non-null).
 */
export function metricDraftProblem(
  draft: TaskTypeMetricDraft,
  numericFields: readonly MetricFieldOption[] | null,
): MetricDraftProblem | null {
  if (draft.label.trim().length > MAX_TASK_TYPE_METRIC_LABEL_LENGTH) {
    return 'label_too_long';
  }
  if (draft.kind !== 'sum') return null;
  if (!draft.field) return 'missing_field';
  const known =
    numericFields === null ||
    numericFields.some((field) => field.fieldName === draft.field);
  return known ? null : 'unknown_field';
}

/**
 * The fields a sum card may total: the input schema's single-value numeric
 * fields plus the linked record's amounts. Null (unknown) while the schema
 * loads or after it failed to load, so nothing is flagged until it is known.
 */
export function effectiveMetricFields(
  schemaStatus: ProcedureInputSchemaStatus,
  inputFields: readonly MetricFieldOption[],
  anchorFields: readonly MetricFieldOption[],
): MetricFieldOption[] | null {
  if (schemaStatus === 'loading' || schemaStatus === 'error') return null;
  return [...numericMetricFields(inputFields), ...anchorFields];
}

/**
 * True when the list can be saved (within the card cap, every card complete,
 * every sum card on a field in `numericFields` once that is known).
 */
export function areMetricDraftsValid(
  drafts: readonly TaskTypeMetricDraft[],
  numericFields: readonly MetricFieldOption[] | null = null,
): boolean {
  return (
    drafts.length <= MAX_TASK_TYPE_METRICS &&
    drafts.every((draft) => metricDraftProblem(draft, numericFields) === null)
  );
}

export function canAddMetricDraft(
  drafts: readonly TaskTypeMetricDraft[],
): boolean {
  return drafts.length < MAX_TASK_TYPE_METRICS;
}

/** Move the card at `index` one place up (-1) or down (1). */
export function moveMetricDraft(
  drafts: readonly TaskTypeMetricDraft[],
  index: number,
  delta: -1 | 1,
): TaskTypeMetricDraft[] {
  const target = index + delta;
  if (target < 0 || target >= drafts.length) return [...drafts];
  const next = [...drafts];
  const [moved] = next.splice(index, 1);
  if (moved) next.splice(target, 0, moved);
  return next;
}

/** Switch a card's kind; a field only belongs to a sum card. */
export function withMetricKind(
  draft: TaskTypeMetricDraft,
  kind: TaskTypeMetricKind,
): TaskTypeMetricDraft {
  return { ...draft, kind, field: kind === 'sum' ? draft.field : '' };
}
